import { useRef, useCallback, useEffect } from "react";

// Strident repeating "ding-ding-ding" bell via Web Audio API + Screen Wake Lock
export function useKitchenAlerts() {
  const ctxRef = useRef(null);
  const masterRef = useRef(null);
  const loopRef = useRef(null);
  const wakeRef = useRef(null);
  const enabledRef = useRef(false);

  const unlock = useCallback(async () => {
    if (!ctxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      ctxRef.current = new AC();
      // Master gain + compressor -> very loud, consistent alarm
      const master = ctxRef.current.createGain();
      master.gain.value = 6.0;
      const comp = ctxRef.current.createDynamicsCompressor();
      master.connect(comp);
      comp.connect(ctxRef.current.destination);
      masterRef.current = master;
    }
    if (ctxRef.current.state === "suspended") await ctxRef.current.resume();
    enabledRef.current = true;

    try { if (document.documentElement.requestFullscreen) await document.documentElement.requestFullscreen(); } catch {}
    try {
      if ("wakeLock" in navigator) {
        wakeRef.current = await navigator.wakeLock.request("screen");
        document.addEventListener("visibilitychange", async () => {
          if (wakeRef.current !== null && document.visibilityState === "visible") {
            try { wakeRef.current = await navigator.wakeLock.request("screen"); } catch {}
          }
        });
      }
    } catch {}
  }, []);

  const ding = useCallback(() => {
    const ctx = ctxRef.current;
    const master = masterRef.current;
    if (!ctx || !master) return;
    // Loud, harsh, strident "ding-ding-ding"
    [0, 0.15, 0.30].forEach((offset, idx) => {
      const o = ctx.createOscillator();
      const sub = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "square";
      sub.type = "sawtooth";
      o.frequency.value = idx % 2 === 0 ? 1300 : 1850;
      sub.frequency.value = o.frequency.value / 2;
      const t = ctx.currentTime + offset;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(1.0, t + 0.008);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.14);
      o.connect(g); sub.connect(g); g.connect(master);
      o.start(t); sub.start(t);
      o.stop(t + 0.15); sub.stop(t + 0.15);
    });
  }, []);

  const startAlarm = useCallback(() => {
    if (!enabledRef.current || loopRef.current) return;
    ding();
    loopRef.current = setInterval(ding, 1100);
  }, [ding]);

  const stopAlarm = useCallback(() => {
    if (loopRef.current) { clearInterval(loopRef.current); loopRef.current = null; }
  }, []);

  useEffect(() => () => { stopAlarm(); }, [stopAlarm]);

  return { unlock, startAlarm, stopAlarm, isEnabled: () => enabledRef.current };
}
